import { useState, type ReactNode } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTick } from "@wojtekmaj/react-hooks";
import { useTranslation } from "react-i18next";

import { dayjs } from "@thalamos/common";
import type { CreateIncidentMinimumFields } from "@thalamos/vision-common";
import {
  FormSectionSchema,
  getLatestPlaceOfSafetyName,
  getTimeAtPlaceOfSafety,
  IncidentFormTypeSchema,
  tk,
  type IncidentWorkItemWithContext,
} from "@thalamos/vision-common";

import { TypographyI18N } from "../../components/TypographyI18N/TypographyI18N.js";
import { getIncidentStatus } from "../FormProgress/FormProgress.js";
import { formatDuration } from "./helpers/formatDuration.js";

interface IncidentDetailsCardProps {
  incident: IncidentWorkItemWithContext;
  defaultExpanded?: boolean;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        py: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", fontWeight: 500, minWidth: 120 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Resolves the current owner participant from the incident context.
 */
function getOwnerName(
  incident: IncidentWorkItemWithContext,
): string | undefined {
  const owner = incident.participants.find(
    (p) => p.role === "owner" && p.status === "active",
  );
  return owner?.name;
}

function getCadNumber(
  incident: IncidentWorkItemWithContext,
): string | undefined {
  const createIncidentForm = incident.forms.find(
    (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
  );
  if (!createIncidentForm) return undefined;

  const mainSection = createIncidentForm.sections.find(
    (section) => section.id === FormSectionSchema.enum.createIncidentMain,
  );
  if (!mainSection) return undefined;

  return (mainSection.data as CreateIncidentMinimumFields | undefined)
    ?.dispatchNumber.value;
}

function formatDate(date: Date | string): string {
  return dayjs.utc(date).tz("Europe/London").format("DD/MM/YYYY HH:mm");
}

export function IncidentDetailsCard({
  incident,
  defaultExpanded = true,
}: IncidentDetailsCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { incidentWorkItem } = incident;

  // Re-renders each minute so a time at the place of safety that is still running stays current.
  useTick(60_000);

  const placeOfSafetyName = getLatestPlaceOfSafetyName(incident);
  const timeAtPlaceOfSafety = getTimeAtPlaceOfSafety(incident, dayjs());
  const cadNumber = getCadNumber(incident);
  const unknownLabel = t(tk("incidentSummary.unknown"));

  // Determine the incident type label from the form types present
  const formTypes = incident.forms.map((f) => f.formType);
  const incidentTypeKey = formTypes.includes("136")
    ? "incidentWorkItem.type.136"
    : formTypes.includes("mentalCapacityAct")
      ? "incidentWorkItem.type.mca"
      : "incidentWorkItem.type.other";

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        overflow: "visible",
      }}
      data-testid="incident-details-card"
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: 2 } }}>
        {/* Header row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => {
            setExpanded((v) => !v);
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: "primary.main" }} />
            <TypographyI18N
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
              translationKey={tk("incidentSummary.incidentDetails")}
            />
          </Box>
          <IconButton
            size="small"
            aria-label="toggle details"
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.short,
              }),
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto">
          <Divider sx={{ my: 1.5 }} />

          <Stack>
            {cadNumber && (
              <DetailRow
                label={t(tk("incidentSummary.cadNumber"))}
                value={cadNumber}
              />
            )}
            <DetailRow
              label={t(tk("incidentSummary.status"))}
              value={
                <Chip
                  label={getIncidentStatus(incident, t)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              }
            />
            <DetailRow
              label={t(tk("incidentSummary.incidentType"))}
              value={t(tk(incidentTypeKey as Parameters<typeof tk>[0]))}
            />
            <DetailRow
              label={t(tk("incidentSummary.createdAt"))}
              value={formatDate(incidentWorkItem.createdAt)}
            />
            <DetailRow
              label={t(tk("incidentSummary.updatedAt"))}
              value={formatDate(incidentWorkItem.updatedAt)}
            />
            <DetailRow
              label={t(tk("incidentSummary.shareCode"))}
              value={incidentWorkItem.shareCode}
            />
            <DetailRow
              label={t(tk("incidentSummary.assignedOfficer"))}
              value={getOwnerName(incident) ?? unknownLabel}
            />
            {placeOfSafetyName && (
              <DetailRow
                label={t(tk("incidentSummary.placeOfSafety"))}
                value={placeOfSafetyName}
              />
            )}
            {timeAtPlaceOfSafety && (
              <DetailRow
                label={t(tk("incidentSummary.timeAtPlaceOfSafety"))}
                value={
                  timeAtPlaceOfSafety.isOngoing
                    ? t(tk("incidentSummary.timeAtPlaceOfSafetyOngoing"), {
                        duration: formatDuration(timeAtPlaceOfSafety.minutes),
                      })
                    : formatDuration(timeAtPlaceOfSafety.minutes)
                }
              />
            )}
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}
